import { Expose, Transform, Type } from 'class-transformer';
import { Schedule, ProductStatus } from '../../../enums';
import { OrganizationResponseDto } from '../organization/organization-response.dto';

export class ProductResponseDto {
    /**
     * Unique identifier for the product
     * @example 'a1b2c3d4-e5f6-7890-1234-567890abcdef'
     */
    @Expose()
    id: string;

    /**
     * Official name of the product
     * @example 'MediProduct'
     */
    @Expose()
    productName: string;

    /**
     * Alternate name or research code for the product
     * @example 'MPD-123'
     */
    @Expose()
    alternateName: string;

    /**
     * Product item code from MPD
     * @example '7751-1550-000'
     */
    @Expose()
    productItemCode: string;

    /**
     * Fabricator product code
     * @example '7751-0240-231'
     */
    @Expose()
    fabricatorProductCode: string;

    @Expose()
    clientInformationRecordId?: string;

    @Expose()
    clientName?: string;

    @Expose()
    mraStatus: string;

    @Expose()
    picsStatus: string;

    /**
     * Product size (free text)
     * @example '500 mL'
     */
    @Expose()
    size: string;

    /**
     * Product dosage form
     * @example 'Tablet'
     */
    @Expose()
    dosageForm: string;

    /**
     * Drug Identification Number
     * @example '02242893'
     */
    @Expose()
    din: string;

    /**
     * Natural Product Number
     * @example '80025550'
     */
    @Expose()
    npn: string;

    /**
     * List of active ingredients and strengths
     * @example 'Lactic acid 10%, Urea 20%'
     */
    @Expose()
    activeIngredients: string;

    /**
     * Dosage strength / concentration
     * @example '80mg/ml (200 mg/2.5 mL)'
     */
    @Expose()
    dosageStrength: string;

    /**
     * Shelf life in months
     * @example 30
     */
    @Expose()
    shelfLifeMonths: number;

    /**
     * Product schedule
     * @example 'OTC'
     */
    @Expose()
    schedule: Schedule;

    /**
     * Storage conditions as per label
     * @example '2 to 25°C. Product can be refrigerated'
     */
    @Expose()
    labelledStorageConditions: string;

    /**
     * Marketing Authorization Holder organization ID
     * @example 'a1b2c3d4-e5f6-7890-1234-567890abcdef'
     */
    @Expose()
    mahOrganizationId: string;

    /**
     * Marketing Authorization Holder organization (populated via LEFT JOIN)
     */
    @Expose()
    @Type(() => OrganizationResponseDto)
    mahOrganization?: OrganizationResponseDto;

    @Expose()
    uicDescription: string;

    @Expose()
    @Transform(({ value }) => {
        if (!value) return null;
        if (typeof value === 'string') {
            const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
            if (match) return `${match[1]}-${match[2]}-${match[3]}`;
        }
        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            const y = value.getUTCFullYear();
            const m = String(value.getUTCMonth() + 1).padStart(2, '0');
            const d = String(value.getUTCDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        }
        return null;
    })
    uicExpiry: string | null;

    @Expose()
    qcGvpResponsibleForPv: boolean;

    @Expose()
    qcRegulatoryResponsibleForProductLifecycle: boolean;

    /**
     * Product status
     * @example 'DISCONTINUED'
     */
    @Expose()
    discontinuationStatus: ProductStatus;

    /**
     * Product status notes
     * @example 'Product discontinued as of Q2 2024 due to regulatory changes'
     */
    @Expose()
    discontinuationNotes: string;

    /**
     * Universal Product Code or GTIN
     * @example '735005284376'
     */
    @Expose()
    upcGtin: string;

    /**
     * Common batch sizes for this product
     * @example '550 kg, 800 kg, 1600 kg'
     */
    @Expose()
    batchSizes: string;

    /**
     * Details if product is on hold
     * @example 'ON HOLD OCT 7, 2015 at warehouse'
     */
    @Expose()
    onHoldDetails: string;

    /**
     * Flag if product entry refers to reworked material
     * @example false
     */
    @Expose()
    isReworked: boolean;

    /**
     * Details if reworked
     * @example 'Reworked at Promag'
     */
    @Expose()
    reworkDetails: string;

    /**
     * Reference to original product if this is a rework
     * @example 'a1b2c3d4-e5f6-7890-1234-567890abcdef'
     */
    @Expose()
    originalProductId: string;

    /**
     * Notes on Health Canada approval and launch dates
     * @example 'HC approval received March 2023, launched May 2023'
     */
    @Expose()
    approvalLaunchNotes: string;

    /**
     * General comments field
     * @example 'Product needs to be shipped under controlled conditions'
     */
    @Expose()
    generalComments: string;

    /**
     * User who last updated this record
     * @example 'John Smith'
     */
    @Expose()
    lastUpdatedBy: string;

    /**
     * Timestamp when the record was created
     * @example '2024-01-15T10:30:00.000Z'
     */
    @Expose()
    @Type(() => Date)
    createdAt: Date;

    /**
     * Timestamp when the record was last updated
     * @example '2024-01-15T15:45:00.000Z'
     */
    @Expose()
    @Type(() => Date)
    updatedAt: Date;

    /**
     * Major version number
     * @example 2
     */
    @Expose()
    versionMajor: number;

    /**
     * Minor version number
     * @example 3
     */
    @Expose()
    versionMinor: number;

    /**
     * Version string (computed)
     * @example '2.3'
     */
    @Expose()
    versionString: string;
}
